import { AbilityBuilder, createMongoAbility, MongoAbility } from '@casl/ability'
import {
    SmartlyActionTypes,
    SmartlyActionItems,
} from '../../models/permissions'
import {
    User,
    UserHelper,
    UserActionTypes,
} from '../../Techedge-digitalportal-shared'

type AppAbility = MongoAbility<[SmartlyActionTypes, SmartlyActionItems]>

export let ability =
    createMongoAbility<[SmartlyActionTypes, SmartlyActionItems]>()

const defineRulesFor = (user: User, permissions?: any[]) => {
    const { can, cannot, rules } = new AbilityBuilder<AppAbility>(null) // null is not correct

    /****************************************PERMISSIONS****************************************
     * Permissions are retrieved from the backend as an array of objects ***********************
     * Each object is structured as follows:
     * {
     * @param code: a string describing a complex action with a conventional separator ('_')}
     * @param description: a string describing the action as a human readable text
     * @param additionalData: an object that allows us to integrate different conditions applied
     * to the single ability
     * }
     *******************************************************************************************/
    if (permissions) {
        for (let permission of permissions) {
            /**************************SPLITTING PERMISSION CODE ******************************
             * To not overwhelm the backend side logic our frontend accepts a complex string
             * inside the permission.code parameter. This code includes 2 main details separated
             * by an accepted convention ('_'): the actual action of the permission and the
             * entity at which the action is directed at (there's an option third detail that
             * describes also an eventual entity type). This allows us to better structure our
             * permission objects using the 'can' implementation at its best and therefore making
             * easily readable and visible at a glance in our views what each permission wrapper
             * is pointing at.
             *********************************************************************************/

            // const code = `${permission.actionName}_${permission.projectType}_${permission.projectSubTypeName}`
            // let split: any = permission.code.split('_')
            // let action: string = split[0] // This is the actual action: save, cancel, edit...
            let action: string = permission.actionName // This is the actual action: save, cancel, edit...
            // let item: string = split[1] // This is the entity at which the action is directed at: task, news...
            let item: string = permission.projectType // This is the entity at which the action is directed at: task, news...
            let entityType: any = permission.projectSubTypeName //This is the optional parameter. the entityType describes optional subtypes like the projectType for entities like tasks and news
            let condition: any = {}
            if (entityType) {
                // entityType = split[2] //This is the optional parameter. the entityType describes optional subtypes like the projectType for entities like tasks and news
                switch (item) {
                    case SmartlyActionItems.TASK:
                    case SmartlyActionItems.NEWS:
                    case SmartlyActionItems.VISIT:
                        condition.type = {
                            $eq: entityType,
                        }
                        break
                }
            }
            if (permission.additional_data) {
                let statusList: any[] = permission.additional_data.status_list
                let childrenSubType: any[] =
                    permission.additional_data.childrenSubType
                let userIdCheckField: any = permission.additional_data.userIdCheckField
                let projectIdCheckField: any =
                    permission.additional_data.projectIdCheckField
                let roleCheckField: any =
                    permission.additional_data.roleCheckField
                let assignments: any[] = permission.additional_data.assignment
                let recipient: any[] = permission.additional_data.recipient
                let specialCollection: boolean =
                    permission.additional_data.specialCollection

                if (statusList?.length) {
                    condition.status = {
                        $in: statusList,
                    }
                }
                if (childrenSubType?.length) {
                    condition.childrenSubType = {
                        $in: childrenSubType,
                    }
                }

                if (assignments?.length) {
                    condition.assignment = {
                        $in: assignments,
                    }
                }

                if (roleCheckField) {
                    condition[roleCheckField] = {
                        $in: [user.role],
                    }
                }

                if (recipient?.length) {
                    condition.recipient = {
                        $eq: user.application_profiles.find(
                            (el: any) => el.application_code === entityType
                        ).profile,
                    }
                }

                if (specialCollection === true) {
                    condition = {
                        $eq: 'special_collection',
                    }
                }

                can(`${action}` as any, item as SmartlyActionItems, condition)

                if (userIdCheckField) {
                    cannot(`${action}` as any, item as SmartlyActionItems, {
                        [userIdCheckField]: { $exists: true },
                        [userIdCheckField]: { $ne: user.user_id as any },
                    })
                }
                if (projectIdCheckField) {
                    cannot(`${action}` as any, item as SmartlyActionItems, {
                        [projectIdCheckField]: { $exists: true },
                    })
                }
                if (specialCollection === false) {
                    cannot(
                        `${action}` as any,
                        item as SmartlyActionItems,
                        'special_collection'
                    )
                }
            } else {
                can(`${action}` as any, item as SmartlyActionItems, condition)
            }
        }
    }
    return rules
}

export const setAbility = (actions: any[], userId: any) => {
    ability.update(defineRulesFor(userId, actions))
    UserHelper._shared().store.dispatch({
        type: UserActionTypes.SMARTLY_PERMISSION_UPDATED,
    })
}
